import axios from "@/plugins/axios";
import { actionTypes, mutationTypes } from "./types";
import { showMessage } from "@/helpers/messages";
import { mutationTypes as flashMessagesMutationTypes } from "@/store/FlashMessages/types";
import { MESSAGE_STATE } from "@/store/FlashMessages/state";

function addPaymentRight(permissions, publisher) {
    const viewPayment = {
        type: "right",
        name: "view_payment",
        id: "",
        target: { admin: false, publisher: publisher, website: "all" },
    };
    permissions.push(viewPayment);
    return permissions;
}

function getBadInviteMessage(email) {
    return {
        header: `Invite was not sent to user ${email}.`,
        description: "Please check if email exists or try again later.",
        messageState: MESSAGE_STATE.WARN,
    };
}

export const actions = {
    async [actionTypes.getData]({ commit }) {
        commit(mutationTypes.usersGetDataStart);
        return axios({
            method: "get",
            url: "sql/manage-users",
        }).then(
            (data) => {
                commit(mutationTypes.usersGetDataSuccess, data.data);
            },
            () => {
                commit(mutationTypes.usersGetDataFailure);
            }
        );
    },
    async [actionTypes.getAllData]({ commit }, { page = 1, searchQuery = "" } = {}) {
        commit(mutationTypes.usersGetAllDataStart);

        let url = page > 1 ? `sql/manage-users/all/${page}` : "sql/manage-users/all";

        if (searchQuery.trim()) {
            const separator = url.includes("?") ? "&" : "?";
            url += `${separator}q=${encodeURIComponent(searchQuery)}`;
        }

        return axios({
            method: "get",
            url: url,
        }).then(
            (data) => {
                commit(mutationTypes.usersGetAllDataSuccess, data.data);
            },
            () => {
                commit(mutationTypes.usersGetAllDataFailure);
            }
        );
    },
    async [actionTypes.addData]({ commit }, { name, email, role_code, type, publisher, showPayments, addToPublisher }) {
        commit(mutationTypes.userAddStart);
        let permissions = [
            {
                type: "group",
                name: "account",
                id: "",
                target: { admin: false, publisher: publisher },
            },
        ];
        let body = {
            action: "create",
            user: {
                id: "",
                email: email,
                name: name,
                role_code: role_code,
                type: type,
                permissions: permissions,
                brs_domains: [],
                is_lite: true,
            },
        };

        if (addToPublisher) {
            body["addToPublisher"] = publisher;
        }

        if (showPayments) {
            permissions = addPaymentRight(permissions, publisher);
        }

        return axios({
            method: "post",
            url: "sql/manage-users/user",
            data: body,
        }).then(
            (data) => {
                showMessage(data.data, "creat", "user");
                if (data.data.ok) {
                    commit(mutationTypes.userAddSuccess, {
                        ...data.data.user,
                        right_group_id: data.data.right_group_updated?.id || null,
                    });
                } else {
                    commit(mutationTypes.userAddEditErrors, data.data.error);
                }
                return data;
            },
            () => {
                commit(mutationTypes.userAddFailure);
            }
        );
    },
    async [actionTypes.editData]({ commit }, { id, name, email, role_code, type, right_group_id }) {
        commit(mutationTypes.userEditStart);
        let body = {
            action: "edit",
            user: {
                id: id,
                email: email,
                role_code: role_code,
                name: name,
                type: type,
                right_group_id: right_group_id,
            },
        };

        return axios({
            method: "put",
            url: "sql/manage-users/user",
            data: body,
        }).then(
            (data) => {
                showMessage(data.data, "sav", "user");
                if (data.data.ok) {
                    commit(mutationTypes.replaceUserInStore, { user: { id: id }, replaceData: body.user });
                } else {
                    commit(mutationTypes.userAddEditErrors, data.data.error);
                }
            },
            () => {
                commit(mutationTypes.userEditFailure);
            }
        );
    },
    async [actionTypes.deleteData]({ commit }, { id }) {
        commit(mutationTypes.userDeleteStart);
        return axios({
            method: "delete",
            url: `sql/manage-users/user/${id}`,
        }).then(
            (data) => {
                showMessage(data.data, "delet", "user");
                commit(mutationTypes.userDeleteSuccess, data.data);
            },
            () => {
                commit(mutationTypes.userDeleteFailure);
            }
        );
    },
    async [actionTypes.inviteUser]({ commit }, { id, email }) {
        commit(mutationTypes.userInviteStart);
        return axios({
            method: "post",
            url: `sql/manage-users/invite/${id}`,
        }).then(
            (data) => {
                if (data.data && data.data["invite"]) {
                    commit(mutationTypes.userInviteSuccess);
                    commit(flashMessagesMutationTypes.addMessageToQueue, {
                        header: `Invite was sent to user ${email}`,
                        description: "",
                        messageState: MESSAGE_STATE.SUCCESS,
                    });
                } else {
                    commit(flashMessagesMutationTypes.addMessageToQueue, getBadInviteMessage(email));
                }
            },
            () => {
                commit(mutationTypes.userInviteFailure);
                commit(flashMessagesMutationTypes.addMessageToQueue, getBadInviteMessage(email));
            }
        );
    },
    async [actionTypes.removeRightGroup]({ commit }, { right_group_id }) {
        commit(mutationTypes.removeRightGroupStart);

        const payload = {
            right_group_id: right_group_id,
        };

        return axios({
            method: "post",
            url: `sql/manage-users/remove-permission`,
            data: payload,
        }).then(
            (data) => {
                if (data.data.ok === true) {
                    commit(mutationTypes.removeRightGroupSuccess, data.data);
                    commit(flashMessagesMutationTypes.addMessageToQueue, {
                        header: `Permission removed.`,
                        description: "",
                        messageState: MESSAGE_STATE.SUCCESS,
                    });
                } else {
                    commit(flashMessagesMutationTypes.addMessageToQueue, {
                        header: `Permission not removed.`,
                        description: "",
                        messageState: MESSAGE_STATE.WARN,
                    });
                }
            },
            () => {
                commit(mutationTypes.removeRightGroupFailure);
                commit(flashMessagesMutationTypes.addMessageToQueue, {
                    header: `Permission not removed.`,
                    description: "",
                    messageState: MESSAGE_STATE.WARN,
                });
            }
        );
    },
    async [actionTypes.addRightGroup]({ commit }, { user_id, publisher_id, role_code }) {
        commit(mutationTypes.addRightGroupStart);

        const payload = {
            user_id: user_id,
            publisher_id: publisher_id,
            role_code: role_code,
        };

        return axios({
            method: "post",
            url: `sql/manage-users/add-permission`,
            data: payload,
        }).then(
            (data) => {
                if (data.data.ok === true) {
                    commit(mutationTypes.addRightGroupSuccess, data.data);
                    commit(flashMessagesMutationTypes.addMessageToQueue, {
                        header: `Permission added successfully.`,
                        description: "",
                        messageState: MESSAGE_STATE.SUCCESS,
                    });
                } else {
                    commit(flashMessagesMutationTypes.addMessageToQueue, {
                        header: `Permission not added.`,
                        description: data.data.error || "",
                        messageState: MESSAGE_STATE.WARN,
                    });
                }
            },
            () => {
                commit(mutationTypes.addRightGroupFailure);
                commit(flashMessagesMutationTypes.addMessageToQueue, {
                    header: `Permission not added.`,
                    description: "",
                    messageState: MESSAGE_STATE.WARN,
                });
            }
        );
    },
};
